import { COMMON_CONSTANTS } from '@viking-eng/utils';
import PropTypes from 'prop-types';

const { CARD_VARIANTS, IMAGE_FIT } = COMMON_CONSTANTS;

const { array, arrayOf, bool, element, func, instanceOf, number, objectOf, oneOf, oneOfType, shape, string } =
  PropTypes;

const image = {
  alt: string.isRequired,
  baseUrl: string,
  bucket: string,
  className: string,
  containerRef: oneOfType([func, shape({ current: instanceOf(element) })]),
  errorSrc: string,
  fit: oneOf(Object.values(IMAGE_FIT)),
  height: number,
  id: string,
  imageKey: string,
  isFullWidth: bool,
  isResponsive: bool,
  src: string,
  tabIndex: number,
  useFallbackImage: bool,
  width: number,
  widths: PropTypes.arrayOf(PropTypes.number),
};

const jsx = oneOfType([arrayOf(element), element, string]);

const tier = oneOf(['xs', 'sm', 'md', 'lg', 'xl']);

const responsiveImages = arrayOf(
  shape({
    tier,
    ...image,
  })
);

const tile = shape({
  id: string.isRequired,
  title: string.isRequired,
  thumbnailUrl: string.isRequired,
});

const cardHome = shape({
  id: string.isRequired,
  title: string.isRequired,
  thumbnailUrl: string.isRequired,
});

const button = shape({
  disabled: bool,
  onButtonClick: func,
  subText: string,
  text: string,
  url: string,
});

const checkbox = shape({
  id: string,
  label: string,
  input: shape({}),
  title: string,
  value: oneOfType([string, number, bool]),
  isDisabled: bool,
  dataId: string,
});

const baseCard = {
  id: string.isRequired,
  title: string.isRequired,
  thumbnailUrl: string,
  loading: bool,
  imageBadgeText: string,
  imageTags: arrayOf(shape({ text: string, className: string })),
  images: responsiveImages,
  defaultClick: shape({
    onClick: func,
  }),
  noBorder: bool,
  className: string,
};

const card = {
  ...baseCard,
  subTitle: string,
  description: string,
  infoIcons: array,
  checkbox,
  primaryButtonText: string,
  primaryButtonSubtext: string,
  onPrimaryButtonClick: func,
  onSecondaryButtonClick: func,
};

const address = shape({
  addressLine1: string,
  addressLine2: string,
  city: string,
  state: string,
  zip: string,
});

const alert = oneOfType([
  string,
  shape({
    alert: string,
    alertButton: shape({
      buttonText: string,
      onButtonClick: func,
    }),
    alertEndText: string,
  }),
]);

const notification = shape({
  button,
  id: string,
  icon: string,
  text: string,
});

const itineraryDay = {
  action: func,
  active: bool,
  city: string.isRequired,
  description: string,
  heading: string.isRequired,
  number: oneOfType([number, string]).isRequired,
};

const airItinerarySegment = shape({
  city: string,
  cityCode: string,
  date: string,
  time: string,
});

const imageTile = {
  alt: string,
  image: string,
  url: string,
  disabled: bool,
  errorSrc: string,
};

const progressPage = shape({
  text: string,
  disabled: bool,
  selected: bool,
});

const flightSegment = shape({
  // eTicket: string,
  airClassDescription: string,
  airlineBookingNumber: string,
  airlineName: string,
  airlineWebSite: string,
  arrivalCity: string,
  arrivalCityCode: string,
  arrivalDate: string,
  arrivalTime: string,
  departureCity: string,
  departureCityCode: string,
  departureDate: string,
  departureTime: string,
  duration: number,
  flightNumber: string,
  isSegment: bool,
  overNight: number,
  referenceNumber: string,
  seat: string,
  stopoverDuration: number,
  airplaneType: string,
  layoverDuration: number,
  layoverCity: string,
  operatedBy: string,
  threeFlightClasses: bool,
});

const flightDetailsSegment = shape({
  wifi: bool,
  food: bool,
  beverage: bool,
  usb: bool,
  power: bool,
  entertaiment: bool,
  carryOn: shape({
    free: bool,
    weight: number,
  }),
  allowance: arrayOf(
    shape({
      pieces: number,
      weight: number,
      free: bool,
    })
  ),
});

const titleValuePair = PropTypes.shape({
  title: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string), PropTypes.number]),
});

const VikingPropTypes = {
  ...PropTypes,

  airItineraryLabels: shape({
    arrives: string,
    departs: string,
    flight: string,
    seats: string,
    passengers: string,
    manageFlight: string,
  }),

  airItinerary: shape({
    flight: shape({
      flightNumber: string,
      airlineName: string,
      duration: number,
      airlineBookingNumber: string,
    }),
    departs: airItinerarySegment,
    arrives: airItinerarySegment,
    passengers: arrayOf(
      shape({
        passengerNumber: number,
        eTicket: string,
      })
    ),
    seats: arrayOf(
      shape({
        seat: string,
        passengerNumber: number,
        airClassDescription: string,
      })
    ),
  }),

  alert,
  alerts: arrayOf(alert),
  notification,
  notifications: arrayOf(notification),

  button,

  baseCard,
  card: shape({
    ...card,
  }),

  cardSections: arrayOf(
    shape({
      title: string,
      cards: arrayOf(
        shape({
          ...card,
        })
      ),
      bannerNotification: alert,
      preContent: jsx,
    })
  ),

  cardVariant: oneOf(Object.values(CARD_VARIANTS)),

  checkbox,

  dropdownPosition: oneOf(['left', 'right', 'up', 'down']),

  errorCodes: objectOf(string),

  errorMessages: objectOf(string),

  flightSegment,

  flightDetailsSegment,

  gridDirection: oneOf(['horizontal', 'vertical']),

  icon: shape({
    label: string.isRequired,
    name: string.isRequired,
  }),

  iconPanelIcons: arrayOf(
    shape({
      count: number,
      onClick: func,
      variant: string,
    })
  ),

  image,

  input: arrayOf(
    shape({
      groupName: string,
      id: string.isRequired,
      label: jsx.isRequired,
    })
  ),

  itineraryDay,

  itineraryDays: arrayOf(shape(itineraryDay)),

  jsx,

  labelValue: shape({
    label: string,
    value: string,
  }),

  menuItems: arrayOf(
    shape({
      id: string.isRequired,
      isSmallText: string.isRequired,
      openInNewWindow: string,
      title: string.isRequired,
      url: string.isRequired,
    })
  ),

  progressPage,

  reduxFormInput: shape({
    checked: bool,
    name: string,
    onBlur: func,
    onChange: func,
    onDragStart: func,
    onDrop: func,
    onFocus: func,
  }),

  reduxFormMeta: shape({
    active: bool,
    autoFilled: bool,
    asyncValidating: bool,
    dirty: bool,
    dispatch: func,
    error: string,
    form: string,
    invalid: bool,
    pristine: bool,
    submitting: bool,
    submitFailed: bool,
    touched: bool,
    valid: bool,
    visited: bool,
    warning: string,
  }),

  responsiveContent: arrayOf(
    shape({
      content: jsx.isRequired,
      tier,
    })
  ),

  responsiveImages,

  tabs: arrayOf(
    shape({
      active: bool,
      id: string,
      title: string,
      url: string,
    })
  ),

  tier,

  tile,

  tileGrid: arrayOf(tile),
  cardGrid: arrayOf(cardHome),

  paymentMethodRowHeader: shape({
    title: string.isRequired,
    subtitle: string,
    button: shape({
      text: string.isRequired,
      attributes: string,
    }),
  }),

  address,

  paymentMethodSavedItem: shape({
    accountName: string.isRequired,
    maskedAccountNumber: string,
    accountHolderName: string,
    billingAddress: address,
    buttons: arrayOf(shape({ text: string })),
  }),

  itineraryItem: {
    dayValue: string,
    description: string,
  },

  history: shape({
    push: PropTypes.func.isRequired,
  }),

  heroImages: arrayOf(
    shape({
      tier: tier.isRequired,
      caption: jsx,
      src: string,
      alt: string,
    })
  ),

  imageTile,

  imageTiles: arrayOf(shape(imageTile)),

  homeBookingSummary: {
    bookingNumber: titleValuePair,
    dates: titleValuePair,
    daysToGo: titleValuePair,
    direction: titleValuePair,
    guests: titleValuePair,
  },
  customPageLink: PropTypes.shape({
    title: string,
    url: string,
    onClick: func,
  }),
};

export default VikingPropTypes;
